import configData from '../configure';

/**
 * Resolves assigned classes/centers for a Faculty user in KV Org ('62d3b1f4-e453-4933-bfcf-7b5adf5b4e83')
 * Traversing Samanvaykarta -> Paryavekshak -> Nirvahak hierarchy.
 * Returns null if user is Admin or not in KV Org.
 * Returns string[] (can be empty array []) if user is Faculty.
 */
export const getFacultyAssignedClassNames = async (orgId, user, axiosInstance) => {
  const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
  if (!isCustomOrg || !user) return null;

  const rawUser = user.user || user.rawData?.user || user.rawData || user;
  const rawFacultyInfo = user.facultyInfo || user.rawData?.facultyInfo || rawUser.facultyInfo || {};
  const rawAdditional = user.additionalAttributes || user.rawData?.additionalAttributes || rawUser.additionalAttributes || {};

  const role = (user.role || rawUser.role || user.userType || rawUser.userType || '').toUpperCase();
  let facultyType = (rawFacultyInfo.facultyType || user.facultyType || rawUser.facultyType || role || '').toUpperCase();

  // If explicitly ADMIN, return null (allow admin all classes)
  if (role === 'ADMIN' || facultyType === 'ADMIN') {
    return null;
  }

  const userId = user.userId || rawUser.userId || user.id || rawUser.id;
  const email = (user.email || rawUser.email || '').toLowerCase();
  const empId = (rawFacultyInfo.employeeId || '').toUpperCase();

  try {
    const response = await axiosInstance.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
      params: { userType: 'FACULTY', page: 1, pageSize: 500 },
      headers: { accept: 'application/hal+json' }
    });

    const rawFacultyList = Array.isArray(response.data)
      ? response.data
      : (response.data?.items || response.data?.content || []);

    // Try to find current logged-in user in rawFacultyList to resolve their exact facultyType & userId
    const selfInList = rawFacultyList.find(f => {
      const fUserId = f.userId || f.id || f.user?.userId || f.user?.id;
      const fEmail = (f.email || f.user?.email || '').toLowerCase();
      const fEmpId = (f.facultyInfo?.employeeId || f.user?.facultyInfo?.employeeId || '').toUpperCase();
      return (
        (userId && String(fUserId) === String(userId)) ||
        (email && fEmail === email) ||
        (empId && fEmpId === empId)
      );
    });

    if (selfInList) {
      const listFType = (selfInList.facultyInfo?.facultyType || selfInList.user?.facultyInfo?.facultyType || selfInList.facultyType || selfInList.userType || '').toUpperCase();
      if (listFType) facultyType = listFType;
    }

    // Helper to get supervisor ID from any faculty item
    const getSupId = (f) => {
      return (
        f.facultyInfo?.supervisorId ||
        f.user?.facultyInfo?.supervisorId ||
        f.supervisorId ||
        f.additionalAttributes?.supervisorId ||
        f.user?.additionalAttributes?.supervisorId ||
        ''
      );
    };

    // Helper to get facultyType from any faculty item
    const getFType = (f) => {
      return (
        f.facultyInfo?.facultyType ||
        f.user?.facultyInfo?.facultyType ||
        f.facultyType ||
        f.userType ||
        ''
      ).toUpperCase();
    };

    // Self IDs to match against supervisorId
    const myIds = new Set();
    if (userId) myIds.add(String(userId));
    if (selfInList) {
      const selfId = selfInList.userId || selfInList.id || selfInList.user?.userId;
      if (selfId) myIds.add(String(selfId));
      const selfEmp = selfInList.facultyInfo?.employeeId || selfInList.user?.facultyInfo?.employeeId;
      if (selfEmp) myIds.add(String(selfEmp));
    }

    let targetNirvahakUsers = [];

    if (facultyType === 'NIRVAHAK') {
      // 1. NIRVAHAK: Only self
      targetNirvahakUsers = selfInList ? [selfInList] : [user];
    } else if (facultyType === 'PARYAVEKSHAK') {
      // 2. PARYAVEKSHAK: Nirvahaks whose supervisorId matches any of my IDs
      targetNirvahakUsers = rawFacultyList.filter(f => {
        const fType = getFType(f);
        const supId = String(getSupId(f));
        return fType === 'NIRVAHAK' && myIds.has(supId);
      });
    } else if (facultyType === 'SAMANVAYKARTA') {
      // 3. SAMANVAYKARTA: Find Paryavekshaks supervised by Samanvaykarta, then Nirvahaks supervised by those Paryavekshaks
      const paryavekshaks = rawFacultyList.filter(f => {
        const fType = getFType(f);
        const supId = String(getSupId(f));
        return fType === 'PARYAVEKSHAK' && myIds.has(supId);
      });

      const paryavekshakIds = new Set();
      paryavekshaks.forEach(p => {
        const pId = p.userId || p.id || p.user?.userId;
        if (pId) paryavekshakIds.add(String(pId));
        const pEmp = p.facultyInfo?.employeeId || p.user?.facultyInfo?.employeeId;
        if (pEmp) paryavekshakIds.add(String(pEmp));
      });

      targetNirvahakUsers = rawFacultyList.filter(f => {
        const fType = getFType(f);
        const supId = String(getSupId(f));
        return fType === 'NIRVAHAK' && paryavekshakIds.has(supId);
      });
    } else {
      // Not a recognized hierarchy role, return null
      return null;
    }

    // Extract assignedClasses strings from target Nirvahaks
    const assignedClassSet = new Set();
    targetNirvahakUsers.forEach(n => {
      const uAttr = n.user?.additionalAttributes || n.additionalAttributes || {};
      const fInfo = n.facultyInfo || n.user?.facultyInfo || {};
      const assignedStr = uAttr.assignedClasses || fInfo.assignedClasses || fInfo.responsibilities?.[0] || n.assignedClasses || '';

      if (assignedStr) {
        assignedStr.split(',').forEach(c => {
          const clean = c.trim();
          if (clean) assignedClassSet.add(clean);
        });
      }
    });

    return Array.from(assignedClassSet);
  } catch (err) {
    console.error('Error resolving faculty hierarchy assigned classes:', err);
    return null;
  }
};

/**
 * Filter classes dropdown list for faculty in KV Org
 */
export const filterClassesForFaculty = (allClasses = [], assignedClassNames) => {
  if (assignedClassNames === null) return allClasses;
  if (assignedClassNames.length === 0) return [];

  const lowerAssigned = assignedClassNames.map(c => c.toLowerCase());
  let filtered = allClasses.filter(cls => {
    const val = String(cls.value || cls.classId || cls.id || '').toLowerCase();
    const lbl = String(cls.label || cls.className || '').toLowerCase();
    return lowerAssigned.includes(val) || lowerAssigned.includes(lbl);
  });

  // Append custom assigned center names if not present in raw org class metadata
  assignedClassNames.forEach(name => {
    const exists = filtered.some(
      c =>
        String(c.value || c.classId || '').toLowerCase() === name.toLowerCase() ||
        String(c.label || c.className || '').toLowerCase() === name.toLowerCase()
    );
    if (!exists) {
      filtered.push({
        value: name,
        label: name,
        classId: name,
        className: name,
      });
    }
  });

  return filtered;
};

/**
 * Filter students list strictly for faculty in KV Org
 */
export const filterStudentsForFaculty = (students = [], assignedClassNames) => {
  if (assignedClassNames === null) return students;
  if (assignedClassNames.length === 0) return [];

  const lowerAssigned = assignedClassNames.map(c => c.toLowerCase());
  return students.filter(stu => {
    const rawUser = stu.user || stu.rawData?.user || stu;
    const rawStudent = stu.studentInfo || stu.rawData?.studentInfo || {};

    const stuClassId = String(stu.classId || rawStudent.classId || stu.classDetails?.classId || '').toLowerCase();
    const stuClassName = String(stu.className || rawStudent.className || stu.classDetails?.className || '').toLowerCase();
    const stuSection = String(stu.sectionName || rawStudent.sectionName || stu.classDetails?.sectionName || '').toLowerCase();
    const stuFullClass = stuSection ? `${stuClassName} ${stuSection}`.trim() : stuClassName;

    return lowerAssigned.some(assigned => {
      return (
        assigned === stuClassId ||
        assigned === stuClassName ||
        assigned === stuFullClass ||
        stuFullClass.includes(assigned) ||
        assigned.includes(stuClassName)
      );
    });
  });
};
